import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { PanelHeading } from './PanelHeading';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';

interface SeverityDonutChartProps {
  averageConductScore: number;
  strainScore?: number;
  harmScore?: number;
}

// Define exact colors from design
const COLORS = {
  STRAIN: '#8B5CF6', // Purple
  HARM: '#9B1B3C', // Dark red/burgundy
  BACKGROUND: '#F3F4F6', // Light gray
  OUTER_RING: '#E5E7EB' // Lighter gray for outer ring
};

export const SeverityDonutChart: React.FC<SeverityDonutChartProps> = React.memo(({
  averageConductScore,
  strainScore,
  harmScore
}) => {
  // If there's no average conduct score, show 0 for all values
  const hasData = averageConductScore !== undefined && averageConductScore !== null && averageConductScore > 0;
  
  // The scores are already percentages from the backend (e.g., 62.4 means 62.4%)
  // Use 0 instead of defaulting when there's no data
  const strainPercentage = hasData ? (strainScore ?? 0) : 0;
  const harmPercentage = hasData ? (harmScore ?? 0) : 0;
  
  // Convert percentages to 1-10 scale for display (not used in current display but kept for consistency)
  const actualStrainScore = strainPercentage ? Math.round(strainPercentage / 10) : 0;
  const actualHarmScore = harmPercentage ? Math.round(harmPercentage / 10) : 0;
  
  // Data for strain ring (outer) - visual representation
  const strainData = [
    { value: strainPercentage, fill: COLORS.STRAIN },
    { value: 100 - strainPercentage, fill: '#F3F4F6' }
  ];
  
  // Data for harm ring (inner) - visual representation
  const harmData = [
    { value: harmPercentage, fill: COLORS.HARM },
    { value: 100 - harmPercentage, fill: '#F3F4F6' }
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          Average conduct score
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="relative">
        {/* Main chart container */}
        <div className="relative w-[140px] h-[140px] mx-auto">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              {/* Strain ring (outer) */}
              <Pie
                data={strainData}
                cx="50%"
                cy="50%"
                innerRadius={48}
                outerRadius={60}
                startAngle={90}
                endAngle={-270}
                dataKey="value"
                strokeWidth={0}
              >
                {strainData.map((entry, index) => (
                  <Cell key={`strain-${index}`} fill={entry.fill} />
                ))}
              </Pie>
              
              {/* Harm ring (inner) with spacing */}
              <Pie
                data={harmData}
                cx="50%"
                cy="50%"
                innerRadius={32}
                outerRadius={44}
                startAngle={90}
                endAngle={-270}
                dataKey="value"
                strokeWidth={0}
              >
                {harmData.map((entry, index) => (
                  <Cell key={`harm-${index}`} fill={entry.fill} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          
          {/* Center text */}
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-[25px] font-bold text-[#252528]" style={{ fontFamily: 'Open Sans, sans-serif' }}>
              {averageConductScore != null ? averageConductScore.toFixed(1) : 0}
            </span>
          </div>
        </div>
        
        {/* Score details on the right */}
        <div className="absolute top-[35px] right-[10px] text-right">
          <div className="mb-3">
            <div className="text-[26px] font-bold text-[#252528]" style={{ fontFamily: 'Open Sans, sans-serif' }}>
              {strainPercentage.toFixed(1)}
            </div>
            <div className="text-[10px] text-[#8B5CF6] font-semibold" style={{ fontFamily: 'Open Sans, sans-serif' }}>
              Strain
            </div>
          </div>
          <div>
            <div className="text-[26px] font-bold text-[#252528]" style={{ fontFamily: 'Open Sans, sans-serif' }}>
              {harmPercentage.toFixed(1)}
            </div>
            <div className="text-[10px] text-[#9B1B3C] font-semibold" style={{ fontFamily: 'Open Sans, sans-serif' }}>
              Harm
            </div>
          </div>
        </div>
      </div>
      
      {/* Legend */}
      <div className="flex items-center justify-center gap-4 mt-4">
        <div className="flex items-center gap-[5px]">
          <div 
            className="w-[12px] h-[12px] rounded-full"
            style={{ backgroundColor: COLORS.STRAIN }}
          />
          <span className="text-[6px] font-semibold text-[#252528]" style={{ fontFamily: 'Open Sans, sans-serif' }}>
            Strain
          </span>
        </div>
        <div className="flex items-center gap-[5px]">
          <div 
            className="w-[12px] h-[12px] rounded-full"
            style={{ backgroundColor: COLORS.HARM }}
          />
          <span className="text-[6px] font-semibold text-[#252528]" style={{ fontFamily: 'Open Sans, sans-serif' }}>
            Harm
          </span>
        </div>
        </div>
      </CardContent>
    </Card>
  );
});